'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';

import React, { useState, useEffect, useCallback } from 'react';
import ReactGA from 'react-ga4';

import { CookieConsentContext } from './CookieConsentContext';

const CookieConsentModal = dynamic(() => import('./CookieConsentModal'), { ssr: false });
const CookieSettingsModal = dynamic(() => import('./CookieSettingsModal'), { ssr: false });

const COOKIE_SETTINGS_VERSION = 1;
const COOKIE_SETTINGS_KEY = 'tukiluotsi_cookie_settings';

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GOOGLE_MEASUREMENT_ID;

const SHOW_BANNER_TIMER = 3000

const defaultSettings = [
    {
        key: 'googleAnalytics',
        name: 'Google Analytics',
        enabled: true
    },
];

function CookieConsent({ children }) {
    const [userSettings, setUserSettings] = useState(null);
    const [tempSettings, setTempSettings] = useState([]); //Used in the Cookie Settings Modal only, once the user clicks "Save", the "tempSettings" will be set to "settings"
    const [gaInitialized, setGaInitialized] = useState(false);

    const [showBanner, setShowBanner] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    const t = useTranslations('cookieConsent');

    useEffect(() => {
        if (showBanner) {
            document.body.classList.add('allow-scroll');
        } else {
            document.body.classList.remove('allow-scroll');
        }

        return () => {
            document.body.classList.remove('allow-scroll');
        };
    }, [showBanner]);

    // Applies cookie settings (e.g., initializes GA if enabled)
    const applySettings = useCallback((settingsToApply) => {
        const gaSetting = settingsToApply.find(s => s.key === 'googleAnalytics');
        const gaEnabled = gaSetting?.enabled;

        if (process.env.NODE_ENV === 'production') {
            window[`ga-disable-${MEASUREMENT_ID}`] = !gaEnabled;

            if (gaEnabled && !gaInitialized) {
                ReactGA.initialize(MEASUREMENT_ID);
                setGaInitialized(true);
            }
        }

    }, [gaInitialized]);

    // Load stored settings or show banner if missing
    useEffect(() => {
        const stored = localStorage.getItem(COOKIE_SETTINGS_KEY);
        let parsed = null;

        try {
            parsed = stored ? JSON.parse(stored) : null;
        } catch {
            parsed = null;
        }

        if (parsed?.version === COOKIE_SETTINGS_VERSION) {
            setUserSettings(parsed.settings);
            applySettings(parsed.settings);
        } else {
            // Clear old cookies here

            const timeout = setTimeout(() => {
                setShowBanner(true);
            }, SHOW_BANNER_TIMER);

            return () => clearTimeout(timeout); // Cleanup timer on unmount
        }

    }, [applySettings]);

    const storeCookieSettings = (settings) => {
        localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify({
            version: COOKIE_SETTINGS_VERSION,
            settings
        }));
    };

    const handleAcceptAll = () => {
        storeCookieSettings(defaultSettings)
        setUserSettings(defaultSettings);
        applySettings(defaultSettings);
        setShowBanner(false);
    };

    const handleOpenSettings = () => {
        const settings = userSettings ?? defaultSettings;
        setTempSettings(settings.map(s => ({ ...s })));
        setShowSettingsModal(true);
    };

    const handleToggleTempSetting = (key) => {
        setTempSettings(prev =>
            prev.map(setting =>
                setting.key === key ? { ...setting, enabled: !setting.enabled } : setting
            )
        );
    };

    const handleSavePreferences = () => {
        storeCookieSettings(tempSettings)
        setUserSettings(tempSettings);
        applySettings(tempSettings);
        setShowSettingsModal(false);
        setShowBanner(false);
    };


    return (
        <CookieConsentContext.Provider value={{ openSettings: handleOpenSettings, settings: userSettings }}>
            <>
                {children}

                {showBanner && (
                    <CookieConsentModal
                        t={t}
                        showBanner={showBanner}
                        handleAcceptAll={handleAcceptAll}
                        handleOpenSettings={handleOpenSettings}
                    />
                )}

                {showSettingsModal && (
                    <CookieSettingsModal
                        t={t}
                        showModal={showSettingsModal}
                        tempSettings={tempSettings}
                        handleToggleTempSetting={handleToggleTempSetting}
                        handleSavePreferences={handleSavePreferences}
                        setShowModal={setShowSettingsModal}
                    />
                )}
            </>
        </CookieConsentContext.Provider>
    );
}

export default CookieConsent;